import { Suspense, useCallback, useEffect, useId, useRef, useState, type ComponentType, type SyntheticEvent } from 'react'
import { useNavigate } from 'react-router'
import { activities, runActivityDeepLinkPreflight } from '@src/activities'
import { arrayToCsv, downloadCsv } from '@src/utils/csvUtils'
import { useClipboard } from '@src/hooks/useClipboard'
import type {
  ActivityDeepLinkPreflightConfig,
  ActivityDeepLinkPreflightResult,
  ActivityPersistentLinkBuilderProps,
  ActivityRegistryEntry,
} from '../../../../types/activity.js'
import type { PersistentSessionEntryPolicy } from '../../../../types/waitingRoom.js'
import {
  buildPersistentLinkRequestBody,
  buildPersistentLinkUrl,
  buildManageDashboardUtilityUrl,
  buildPersistentSessionKey,
  storeCreateSessionBootstrapPayload,
  describeSelectedOptions,
  filterPersistentEntryPolicyOptionsForActivity,
  initializeDeepLinkOptions,
  isPersistentLinkPreflightVerified,
  normalizePersistentEntryPolicyForActivity,
  normalizeSelectedOptions,
  normalizeMultiselectValue,
  parseMultiselectValues,
  persistCreateSessionBootstrapToSessionStorage,
  parseDeepLinkGenerator,
  parseDeepLinkOptions,
  resolvePersistentLinkPreflightValue,
  validateDeepLinkSelection,
  type DeepLinkSelection,
} from './manageDashboardUtils'
import {
  buildStandaloneActivityLauncherManagePath,
  buildStandaloneActivityLauncherState,
  createStandaloneActivitySession,
} from './activityLauncherUtils'
import { resolveCustomPersistentLinkBuilder } from './manageDashboardViewUtils'
import {
  getPersistentSessionEntryPolicyDescription,
  getPersistentSessionEntryPolicyLabel,
  PERSISTENT_SESSION_ENTRY_POLICY_OPTIONS,
} from './persistentSessionEntryPolicyUtils'
import Modal from '../ui/Modal'
import Button from '../ui/Button'

type DashboardActivity = ActivityRegistryEntry
const DEFAULT_PERSISTENT_ENTRY_POLICY: PersistentSessionEntryPolicy = 'instructor-required'

function supportsStandalonePermalink(activity: DashboardActivity): boolean {
  return activity.standaloneEntry?.enabled === true && activity.standaloneEntry.supportsPermalink === true
}

interface PersistentSession {
  activityName: string
  hash: string
  fullUrl: string
  entryPolicy?: PersistentSessionEntryPolicy
  teacherCode?: string
  selectedOptions?: Record<string, unknown>
}

interface PersistentSessionListResponse {
  sessions?: PersistentSession[]
}

interface PersistentLinkCreateResponse {
  error?: string
  url?: string
  hash?: string
}

interface CreatedPersistentLinkResult {
  fullUrl: string
  hash: string
  teacherCode: string
  selectedOptions?: DeepLinkSelection
}

function formatPersistentLinkPreflightWarning(warning: string): string {
  return warning.replace(/\s*You can continue anyway\.?\s*$/i, ' Please fix the option and verify again.')
}

const colorClasses: Record<string, string> = {
  blue: 'bg-blue-600',
  green: 'bg-green-600',
  purple: 'bg-purple-600',
  red: 'bg-red-600',
  yellow: 'bg-yellow-600',
  indigo: 'bg-indigo-600',
  orange: 'bg-orange-600',
  emerald: 'bg-emerald-600',
  sky: 'bg-sky-600',
  fuchsia: 'bg-fuchsia-600',
  pink: 'bg-pink-600',
  rose: 'bg-rose-600',
  lime: 'bg-lime-600',
  teal: 'bg-teal-600',
}

const borderColorClasses: Record<string, string> = {
  blue: 'border-blue-200',
  green: 'border-green-200',
  purple: 'border-purple-200',
  red: 'border-red-200',
  yellow: 'border-yellow-200',
  indigo: 'border-indigo-200',
  orange: 'border-orange-200',
  emerald: 'border-emerald-200',
  sky: 'border-sky-200',
  fuchsia: 'border-fuchsia-200',
  pink: 'border-pink-200',
  rose: 'border-rose-200',
  lime: 'border-lime-200',
  teal: 'border-teal-200',
}

const bgColorClasses: Record<string, string> = {
  blue: 'bg-blue-50',
  green: 'bg-green-50',
  purple: 'bg-purple-50',
  red: 'bg-red-50',
  yellow: 'bg-yellow-50',
  indigo: 'bg-indigo-50',
  orange: 'bg-orange-50',
  emerald: 'bg-emerald-50',
  sky: 'bg-sky-50',
  fuchsia: 'bg-fuchsia-50',
  pink: 'bg-pink-50',
  rose: 'bg-rose-50',
  lime: 'bg-lime-50',
  teal: 'bg-teal-50',
}

function getWindowOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : ''
}

function renderPersistentEntryPolicyControl(
  persistentEntryPolicy: PersistentSessionEntryPolicy,
  persistentEntryPolicyOptions: readonly {
    value: PersistentSessionEntryPolicy
    label: string
    description: string
  }[],
  onChange: (entryPolicy: PersistentSessionEntryPolicy) => void,
) {
  return (
    <div>
      <label htmlFor="persistent-entry-policy" className="block text-sm font-semibold text-gray-700 mb-2">
        Entry mode
      </label>
      <select
        id="persistent-entry-policy"
        value={persistentEntryPolicy}
        onChange={(event) => onChange(event.target.value as PersistentSessionEntryPolicy)}
        className="border-2 border-gray-300 rounded px-4 py-2 w-full bg-white focus:outline-none focus:border-blue-500"
      >
        {persistentEntryPolicyOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <p className="text-xs text-gray-500 mt-1">
        {getPersistentSessionEntryPolicyDescription(persistentEntryPolicy)}
      </p>
    </div>
  )
}

interface ManageDashboardProps {
  activityRegistry?: DashboardActivity[]
  runDeepLinkPreflight?: (
    activityId: string,
    preflight: ActivityDeepLinkPreflightConfig,
    rawValue: string,
  ) => Promise<ActivityDeepLinkPreflightResult>
}

export default function ManageDashboard({
  activityRegistry = activities,
  runDeepLinkPreflight = runActivityDeepLinkPreflight,
}: ManageDashboardProps = {}) {
  const navigate = useNavigate()
  const teacherCodeInputId = useId()
  const preflightRequestIdRef = useRef(0)
  const [showPersistentModal, setShowPersistentModal] = useState(false)
  const [selectedActivity, setSelectedActivity] = useState<DashboardActivity | null>(null)
  const [editingPersistentSession, setEditingPersistentSession] = useState<PersistentSession | null>(null)
  const [teacherCode, setTeacherCode] = useState('')
  const [persistentUrl, setPersistentUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [lastPersistentSubmitMode, setLastPersistentSubmitMode] = useState<'created' | 'updated'>('created')
  const [persistentSessions, setPersistentSessions] = useState<PersistentSession[]>([])
  const [savedSessions, setSavedSessions] = useState<Record<string, string>>({})
  const [visibleCodes, setVisibleCodes] = useState<Record<string, boolean>>({})
  const { copyToClipboard, isCopied } = useClipboard()
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [persistentOptions, setPersistentOptions] = useState<DeepLinkSelection>({})
  const [persistentEntryPolicy, setPersistentEntryPolicy] = useState<PersistentSessionEntryPolicy>(
    DEFAULT_PERSISTENT_ENTRY_POLICY,
  )
  const [customBuilderCanSubmit, setCustomBuilderCanSubmit] = useState(true)
  const [isPreflightChecking, setIsPreflightChecking] = useState(false)
  const [preflightWarning, setPreflightWarning] = useState<string | null>(null)
  const [preflightValidatedValue, setPreflightValidatedValue] = useState<string | null>(null)

  const getActivityById = useCallback(
    (activityId: string): DashboardActivity | undefined => {
      return activityRegistry.find((activity) => activity.id === activityId)
    },
    [activityRegistry],
  )

  const getActivityName = useCallback(
    (activityId: string): string => {
      return getActivityById(activityId)?.name || activityId
    },
    [getActivityById],
  )

  const getActivityColor = useCallback(
    (activityId: string): string => {
      return getActivityById(activityId)?.color || 'blue'
    },
    [getActivityById],
  )

  const refreshPersistentSessions = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch('/api/persistent-session/list')
      const payload = (await response.json()) as PersistentSessionListResponse
      const sessions = Array.isArray(payload.sessions) ? payload.sessions : []

      setPersistentSessions(sessions)
      setSavedSessions((previous) => {
        const next = { ...previous }

        for (const session of sessions) {
          if (typeof session.teacherCode !== 'string') continue
          next[buildPersistentSessionKey(session.activityName, session.hash)] = session.teacherCode
        }

        return next
      })
    } catch (refreshError) {
      console.error('Failed to fetch persistent sessions:', refreshError)
      setSavedSessions((previous) => previous)
    }
  }, [])

  useEffect(() => {
    let isCurrent = true
    queueMicrotask(() => {
      if (isCurrent) {
        void refreshPersistentSessions()
      }
    })
    return () => {
      isCurrent = false
    }
  }, [refreshPersistentSessions])

  const createSession = async (activityId: string): Promise<void> => {
    setSessionError(null)

    try {
      const activity = getActivityById(activityId)
      const payload = await createStandaloneActivitySession(activityId)
      const navigationState = activity ? buildStandaloneActivityLauncherState(activity, payload) : null

      persistCreateSessionBootstrapToSessionStorage(activity?.createSessionBootstrap, payload.id, payload)
      if (navigationState != null) {
        storeCreateSessionBootstrapPayload(activityId, payload.id, navigationState.createSessionPayload)
      }

      void navigate(buildStandaloneActivityLauncherManagePath(activityId, payload.id, {}), {
        ...(navigationState ? { state: navigationState } : {}),
      })
    } catch (createError) {
      console.error(createError)
      setSessionError('Could not create session. Please try again.')
      setTimeout(() => setSessionError(null), 5000)
    }
  }

  const openPersistentModal = (activity: DashboardActivity, session: PersistentSession | null = null): void => {
    preflightRequestIdRef.current += 1
    setSelectedActivity(activity)
    setEditingPersistentSession(session)
    setShowPersistentModal(true)
    setTeacherCode(session ? (savedSessions[buildPersistentSessionKey(session.activityName, session.hash)] || '') : '')
    setPersistentUrl(null)
    setError(null)
    setCustomBuilderCanSubmit(false)
    setIsPreflightChecking(false)
    setPreflightWarning(null)
    setPreflightValidatedValue(null)
    setPersistentOptions(
      session
        ? normalizeSelectedOptions(activity.deepLinkOptions, session.selectedOptions || {})
        : initializeDeepLinkOptions(activity.deepLinkOptions),
    )
    setPersistentEntryPolicy(
      normalizePersistentEntryPolicyForActivity(
        session?.entryPolicy || DEFAULT_PERSISTENT_ENTRY_POLICY,
        supportsStandalonePermalink(activity),
      ),
    )
  }

  const closePersistentModal = (): void => {
    preflightRequestIdRef.current += 1
    setShowPersistentModal(false)
    setSelectedActivity(null)
    setEditingPersistentSession(null)
    setTeacherCode('')
    setPersistentUrl(null)
    setError(null)
    setIsCreating(false)
    setCustomBuilderCanSubmit(true)
    setIsPreflightChecking(false)
    setPreflightWarning(null)
    setPreflightValidatedValue(null)
    setLastPersistentSubmitMode('created')
    setPersistentOptions({})
    setPersistentEntryPolicy(DEFAULT_PERSISTENT_ENTRY_POLICY)
  }

  const handlePersistentLinkCreated = useCallback(
    async (activityId: string, result: CreatedPersistentLinkResult): Promise<void> => {
      setError(null)
      setPersistentUrl(result.fullUrl)

      setSavedSessions((previous) => ({
        ...previous,
        [buildPersistentSessionKey(activityId, result.hash)]: result.teacherCode,
      }))

      await refreshPersistentSessions()
    },
    [refreshPersistentSessions],
  )

  const submitPersistentLink = async (): Promise<void> => {
    if (!selectedActivity) {
      setError('No activity selected')
      return
    }

    const optionValidationErrors = validateDeepLinkSelection(selectedActivity.deepLinkOptions, persistentOptions)
    if (Object.keys(optionValidationErrors).length > 0) {
      setError('Please fix the highlighted link options')
      return
    }

    try {
      const selectedOptions = normalizeSelectedOptions(selectedActivity.deepLinkOptions, persistentOptions)
      const usesCustomPersistentLinkBuilder = resolveCustomPersistentLinkBuilder(selectedActivity) != null
      const deepLinkGenerator = usesCustomPersistentLinkBuilder ? null : parseDeepLinkGenerator(selectedActivity.deepLinkGenerator)
      const preflight = deepLinkGenerator?.preflight ?? null
      const normalizedPreflightValue = resolvePersistentLinkPreflightValue(preflight?.optionKey, selectedOptions)

      if (usesCustomPersistentLinkBuilder && !customBuilderCanSubmit) {
        setError('Finish the activity-specific link setup before creating the link.')
        return
      }

      if (!usesCustomPersistentLinkBuilder && preflight != null && normalizedPreflightValue.length > 0 && preflightValidatedValue !== normalizedPreflightValue) {
        setError('Verify the link option before creating the link.')
        return
      }

      setError(null)
      setIsCreating(true)

      const isEditing = editingPersistentSession != null
      const normalizedTeacherCode = teacherCode.trim()
      const existingTeacherCode = isEditing
        ? (savedSessions[buildPersistentSessionKey(editingPersistentSession.activityName, editingPersistentSession.hash)] ?? '').trim()
        : ''
      const createsNewLinkFromTeacherCodeChange = isEditing
        && normalizedTeacherCode.length > 0
        && normalizedTeacherCode !== existingTeacherCode
      const isCreateRequest = !isEditing || createsNewLinkFromTeacherCodeChange
      const endpoint = isCreateRequest
        ? (deepLinkGenerator?.endpoint ?? '/api/persistent-session/create')
        : '/api/persistent-session/update'
      const requestBody = buildPersistentLinkRequestBody({
        activityId: selectedActivity.id,
        hash: isCreateRequest ? undefined : editingPersistentSession?.hash,
        teacherCode,
        selectedOptions,
        entryPolicy: persistentEntryPolicy,
      })

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          isCreateRequest && deepLinkGenerator?.expectsSelectedOptions === false
            ? {
                activityName: requestBody.activityName,
                teacherCode: 'teacherCode' in requestBody ? requestBody.teacherCode : undefined,
                entryPolicy: requestBody.entryPolicy,
              }
            : requestBody,
        ),
      })

      if (!response.ok) {
        let message = 'Failed to create persistent link'
        try {
          const payload = (await response.json()) as PersistentLinkCreateResponse
          if (payload.error) {
            message = payload.error
          }
        } catch {
          // Keep fallback error message if payload is not JSON.
        }

        throw new Error(message)
      }

      const payload = (await response.json()) as PersistentLinkCreateResponse
      if (!payload.url || !payload.hash) {
        throw new Error('Failed to create persistent link')
      }

      const fullUrl = buildPersistentLinkUrl(getWindowOrigin(), payload.url, selectedOptions, deepLinkGenerator)
      const persistedTeacherCode = teacherCode.trim()
      await handlePersistentLinkCreated(selectedActivity.id, {
        fullUrl,
        hash: payload.hash as string,
        teacherCode: persistedTeacherCode,
        selectedOptions,
      })
      setLastPersistentSubmitMode(isCreateRequest ? 'created' : 'updated')
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : String(createError))
    } finally {
      setIsCreating(false)
      setIsPreflightChecking(false)
    }
  }

  const handlePersistentLinkSubmit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault()
    void submitPersistentLink()
  }

  const verifyPersistentLinkPreflight = async (): Promise<void> => {
    if (!selectedActivity) {
      setError('No activity selected')
      return
    }

    const deepLinkGenerator = parseDeepLinkGenerator(selectedActivity.deepLinkGenerator)
    const preflight = deepLinkGenerator?.preflight ?? null
    if (preflight == null) {
      return
    }

    const optionValidationErrors = validateDeepLinkSelection(selectedActivity.deepLinkOptions, persistentOptions)
    if (optionValidationErrors[preflight.optionKey]) {
      setError('Please fix the highlighted link options')
      return
    }

    const selectedOptions = normalizeSelectedOptions(selectedActivity.deepLinkOptions, persistentOptions)
    const normalizedValue = resolvePersistentLinkPreflightValue(preflight.optionKey, selectedOptions)

    if (normalizedValue.length === 0) {
      setError('Enter a value to verify first.')
      return
    }

    setError(null)
    const requestId = preflightRequestIdRef.current + 1
    preflightRequestIdRef.current = requestId
    setIsPreflightChecking(true)
    setPreflightWarning(null)

    try {
      const result = await runDeepLinkPreflight(selectedActivity.id, preflight, normalizedValue)
      if (requestId !== preflightRequestIdRef.current) {
        return
      }
      if (!result.valid) {
        setPreflightValidatedValue(null)
        setPreflightWarning(
          result.warning
            ? formatPersistentLinkPreflightWarning(result.warning)
            : 'Unable to verify this value right now. Please try again.',
        )
        return
      }

      setPreflightValidatedValue(normalizedValue)
      setPreflightWarning(null)
    } catch {
      if (requestId !== preflightRequestIdRef.current) {
        return
      }
      setPreflightValidatedValue(null)
      setPreflightWarning('Unable to verify this value right now. Please try again.')
    } finally {
      if (requestId === preflightRequestIdRef.current) {
        setIsPreflightChecking(false)
      }
    }
  }

  const downloadPersistentLinksCSV = (): void => {
    const headers = ['Activity', 'Entry Mode', 'Teacher Code', 'URL']
    const rows = persistentSessions.map((session) => {
      const sessionKey = buildPersistentSessionKey(session.activityName, session.hash)
      return [
        getActivityName(session.activityName),
        getPersistentSessionEntryPolicyLabel(session.entryPolicy),
        savedSessions[sessionKey] || '',
        buildPersistentLinkUrl('', session.fullUrl, session.selectedOptions || {}, null),
      ]
    })

    const csvContent = arrayToCsv([headers, ...rows])
    downloadCsv(csvContent, 'permanent-links')
  }

  const toggleCodeVisibility = (sessionKey: string): void => {
    setVisibleCodes((previous) => ({
      ...previous,
      [sessionKey]: !previous[sessionKey],
    }))
  }

  const removePersistentLink = async (session: PersistentSession): Promise<void> => {
    if (typeof window !== 'undefined' && !window.confirm(`Remove the permanent link for ${getActivityName(session.activityName)}?`)) {
      return
    }

    const response = await fetch('/api/persistent-session/remove', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        activityName: session.activityName,
        hash: session.hash,
      }),
    })

    if (!response.ok) {
      throw new Error('Failed to remove permanent link')
    }

    const sessionKey = buildPersistentSessionKey(session.activityName, session.hash)
    setSavedSessions((previous) => {
      const next = { ...previous }
      delete next[sessionKey]
      return next
    })
    setVisibleCodes((previous) => {
      const next = { ...previous }
      delete next[sessionKey]
      return next
    })
    await refreshPersistentSessions()
  }

  const selectedActivityOptions = selectedActivity ? parseDeepLinkOptions(selectedActivity.deepLinkOptions) : {}
  const selectedActivityDeepLinkGenerator = selectedActivity ? parseDeepLinkGenerator(selectedActivity.deepLinkGenerator) : null
  const selectedActivityPreflight = selectedActivityDeepLinkGenerator?.preflight ?? null
  const selectedActivityPreflightOptionKey = selectedActivityPreflight?.optionKey ?? null
  const persistentEntryPolicyOptions = selectedActivity
    ? filterPersistentEntryPolicyOptionsForActivity(
      PERSISTENT_SESSION_ENTRY_POLICY_OPTIONS,
      supportsStandalonePermalink(selectedActivity),
    )
    : [...PERSISTENT_SESSION_ENTRY_POLICY_OPTIONS]
  const persistentOptionErrors = selectedActivity
    ? validateDeepLinkSelection(selectedActivity.deepLinkOptions, persistentOptions)
    : {}
  const hasPersistentOptionErrors = Object.keys(persistentOptionErrors).length > 0
  const isPreflightVerified = isPersistentLinkPreflightVerified(
    selectedActivityPreflight?.optionKey,
    persistentOptions,
    preflightValidatedValue,
  )

  const resetPreflightStateForOptionChange = (key: string, nextValue: string): void => {
    if (selectedActivityPreflightOptionKey !== key || nextValue.trim() === preflightValidatedValue) {
      return
    }
    preflightRequestIdRef.current += 1
    setPreflightValidatedValue(null)
    setPreflightWarning(null)
    setIsPreflightChecking(false)
  }

  const existingTeacherCodeForEdit = editingPersistentSession
    ? (savedSessions[buildPersistentSessionKey(editingPersistentSession.activityName, editingPersistentSession.hash)] ?? '').trim()
    : ''
  const createsNewLinkFromTeacherCodeChange = editingPersistentSession != null
    && teacherCode.trim().length > 0
    && teacherCode.trim() !== existingTeacherCodeForEdit
  const CustomPersistentLinkBuilder = resolveCustomPersistentLinkBuilder(selectedActivity) as
    | ComponentType<ActivityPersistentLinkBuilderProps>
    | null
  const usesCustomPersistentLinkBuilder = CustomPersistentLinkBuilder != null
  const activePersistentModalContent = usesCustomPersistentLinkBuilder && selectedActivity ? (
    <form onSubmit={handlePersistentLinkSubmit} className="flex flex-col gap-4">
      <p className="text-gray-700">
        Create a permanent URL that you can use in presentations or bookmark. When anyone visits this URL,
        they'll wait until you start the session with your teacher code.
      </p>

      <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-2">
        <p className="text-sm text-yellow-800">
          <strong>⚠️ Security Note:</strong> This is for convenience, not security. The teacher code is stored in
          your browser cookies and is not encrypted. Do not use sensitive passwords.
        </p>
      </div>

      <div>
        <label htmlFor={teacherCodeInputId} className="block text-sm font-semibold text-gray-700 mb-2">Teacher Code (min. 6 characters)</label>
        <input
          id={teacherCodeInputId}
          type="text"
          value={teacherCode}
          onChange={(event) => setTeacherCode(event.target.value)}
          className="border-2 border-gray-300 rounded px-4 py-2 w-full focus:outline-none focus:border-blue-500"
          placeholder={editingPersistentSession
            ? 'Keep code to update this link, or enter a new code to create a new link'
            : 'Create a Teacher Code for this link'}
          minLength={6}
          required
          autoComplete="off"
        />
        <p className="text-xs text-gray-500 mt-1">
          {editingPersistentSession
            ? 'Changing the teacher code creates a new permanent link hash. Existing links are not replaced.'
            : 'Remember this code! You\'ll need it to start sessions from this link.'}
        </p>
      </div>

      {renderPersistentEntryPolicyControl(
        persistentEntryPolicy,
        persistentEntryPolicyOptions,
        setPersistentEntryPolicy,
      )}
      <Suspense fallback={<p className="text-sm text-gray-600">Loading link builder...</p>}>
        <CustomPersistentLinkBuilder
          activityId={selectedActivity.id}
          teacherCode={teacherCode}
          selectedOptions={persistentOptions}
          editState={editingPersistentSession
            ? {
              hash: editingPersistentSession.hash,
              teacherCode: savedSessions[buildPersistentSessionKey(selectedActivity.id, editingPersistentSession.hash)] || '',
              selectedOptions: editingPersistentSession.selectedOptions || {},
              entryPolicy: editingPersistentSession.entryPolicy,
            }
            : null}
          onSelectedOptionsChange={(nextSelectedOptions) => {
            if (selectedActivityPreflightOptionKey != null) {
              resetPreflightStateForOptionChange(
                selectedActivityPreflightOptionKey,
                nextSelectedOptions[selectedActivityPreflightOptionKey] ?? '',
              )
            }
            setPersistentOptions(nextSelectedOptions)
          }}
          onSubmitReadinessChange={setCustomBuilderCanSubmit}
        />
      </Suspense>

      {error !== null && <p className="text-red-600 text-sm bg-red-50 p-2 rounded">{error}</p>}

      <Button
        type="submit"
        disabled={isCreating || teacherCode.trim().length < 6 || hasPersistentOptionErrors || !customBuilderCanSubmit}
      >
        {isCreating
          ? ((editingPersistentSession == null || createsNewLinkFromTeacherCodeChange) ? 'Creating...' : 'Saving...')
          : (createsNewLinkFromTeacherCodeChange ? 'Create New Link' : (editingPersistentSession ? 'Save Changes' : 'Generate Link'))}
      </Button>
    </form>
  ) : (
    <form onSubmit={handlePersistentLinkSubmit} className="flex flex-col gap-4">
      <p className="text-gray-700">
        Create a permanent URL that you can use in presentations or bookmark. When anyone visits this URL,
        they'll wait until you start the session with your teacher code.
      </p>

      <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-2">
        <p className="text-sm text-yellow-800">
          <strong>⚠️ Security Note:</strong> This is for convenience, not security. The teacher code is stored in
          your browser cookies and is not encrypted. Do not use sensitive passwords.
        </p>
      </div>

      <div>
        <label htmlFor={teacherCodeInputId} className="block text-sm font-semibold text-gray-700 mb-2">Teacher Code (min. 6 characters)</label>
        <input
          id={teacherCodeInputId}
          type="text"
          value={teacherCode}
          onChange={(event) => setTeacherCode(event.target.value)}
          className="border-2 border-gray-300 rounded px-4 py-2 w-full focus:outline-none focus:border-blue-500"
          placeholder={editingPersistentSession
            ? 'Keep code to update this link, or enter a new code to create a new link'
            : 'Create a Teacher Code for this link'}
          minLength={6}
          required
          autoComplete="off"
        />
        <p className="text-xs text-gray-500 mt-1">
          {editingPersistentSession
            ? 'Changing the teacher code creates a new permanent link hash. Existing links are not replaced.'
            : 'Remember this code! You\'ll need it to start sessions from this link.'}
        </p>
      </div>

      <div>
        {renderPersistentEntryPolicyControl(
          persistentEntryPolicy,
          persistentEntryPolicyOptions,
          setPersistentEntryPolicy,
        )}
      </div>

      {selectedActivity !== null && Object.keys(selectedActivityOptions).length > 0 && (
        <div className="border-2 border-gray-200 rounded p-3 bg-gray-50">
          <p className="text-sm font-semibold text-gray-700 mb-2">Link options</p>
          <div className="flex flex-col gap-3">
            {Object.entries(selectedActivityOptions).map(([key, option]) => {
              const optionInputId = `persistent-link-option-${key}`
              const optionErrorId = `${optionInputId}-error`
              const optionError = persistentOptionErrors[key]
              const optionErrorAttributes = optionError === undefined
                ? {}
                : {
                  'aria-describedby': optionErrorId,
                  'aria-invalid': true,
                }
              const updatePersistentOption = (nextValue: string): void => {
                resetPreflightStateForOptionChange(key, nextValue)
                setPersistentOptions((previous) => ({
                  ...previous,
                  [key]: nextValue,
                }))
              }

              return (
                <div key={key} className="text-sm text-gray-700">
                  {option.type === 'multiselect' ? (
                    <p className="block font-semibold mb-1">{option.label || key}</p>
                  ) : (
                    <label
                      htmlFor={optionInputId}
                      className={`${option.type === 'checkbox' ? 'inline-flex items-center gap-2' : 'block'} font-semibold mb-1`}
                    >
                      {option.type === 'checkbox' && (
                        <input
                          id={optionInputId}
                          type="checkbox"
                          checked={(persistentOptions[key] ?? '') === 'true'}
                          onChange={(event) => updatePersistentOption(event.target.checked ? 'true' : 'false')}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          {...optionErrorAttributes}
                        />
                      )}
                      {option.label || key}
                    </label>
                  )}
                {option.type === 'checkbox' ? null : option.type === 'select' ? (
                  <>
                    <select
                      id={optionInputId}
                      value={persistentOptions[key] ?? ''}
                      onChange={(event) => updatePersistentOption(event.target.value)}
                      className="w-full border-2 border-gray-300 rounded px-3 py-2 bg-white"
                      {...optionErrorAttributes}
                    >
                      {(option.options || []).map((entry) => (
                        <option key={entry.value} value={entry.value}>
                          {entry.label}
                        </option>
                      ))}
                    </select>
                    {selectedActivityPreflight?.optionKey === key && (
                      <Button
                        type="button"
                        onClick={() => {
                          void verifyPersistentLinkPreflight()
                        }}
                        disabled={isCreating || isPreflightChecking || Boolean(persistentOptionErrors[key]) || (persistentOptions[key] ?? '').trim().length === 0}
                        variant="outline"
                        className="mt-2 whitespace-nowrap"
                      >
                        {isPreflightChecking ? 'Verifying...' : 'Verify'}
                      </Button>
                    )}
                  </>
                ) : option.type === 'multiselect' ? (
                  <fieldset className="flex flex-col gap-2" {...optionErrorAttributes}>
                    <legend className="sr-only">{option.label || key}</legend>
                    {(() => {
                      const values = new Set(parseMultiselectValues(normalizeMultiselectValue(persistentOptions[key] ?? '', option)))
                      return (option.options || []).map((entry) => (
                        <label key={entry.value} className="inline-flex items-center gap-2 font-normal">
                          <input
                            type="checkbox"
                            checked={values.has(entry.value)}
                            onChange={(event) => {
                              const checked = event.target.checked
                              const previewValues = new Set(values)
                              if (checked) {
                                previewValues.add(entry.value)
                              } else {
                                previewValues.delete(entry.value)
                              }
                              resetPreflightStateForOptionChange(key, Array.from(previewValues).join(','))
                              setPersistentOptions((previous) => {
                                const nextValues = new Set(parseMultiselectValues(normalizeMultiselectValue(previous[key] ?? '', option)))
                                if (checked) {
                                  nextValues.add(entry.value)
                                } else {
                                  nextValues.delete(entry.value)
                                }
                                return {
                                  ...previous,
                                  [key]: Array.from(nextValues).join(','),
                                }
                              })
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            {...optionErrorAttributes}
                          />
                          {entry.label}
                        </label>
                      ))
                    })()}
                  </fieldset>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      id={optionInputId}
                      type={option.type === 'number' ? 'number' : 'text'}
                      min={option.min}
                      max={option.max}
                      step={option.step}
                      value={persistentOptions[key] ?? ''}
                      onChange={(event) => updatePersistentOption(event.target.value)}
                      className={`w-full border-2 rounded px-3 py-2 ${persistentOptionErrors[key] ? 'border-red-400' : 'border-gray-300'}`}
                      {...optionErrorAttributes}
                    />
                    {selectedActivityPreflight?.optionKey === key && (
                      <Button
                        type="button"
                        onClick={() => {
                          void verifyPersistentLinkPreflight()
                        }}
                        disabled={isCreating || isPreflightChecking || Boolean(persistentOptionErrors[key]) || (persistentOptions[key] ?? '').trim().length === 0}
                        variant="outline"
                        className="whitespace-nowrap"
                      >
                        {isPreflightChecking ? 'Verifying...' : 'Verify URL'}
                      </Button>
                    )}
                  </div>
                )}
                {optionError !== undefined && (
                  <span id={optionErrorId} className="block mt-1 text-xs text-red-600">{optionError}</span>
                )}
                {optionError === undefined && selectedActivityPreflight?.optionKey === key && (persistentOptions[key] ?? '').trim().length > 0 && (
                  <span className={`block mt-1 text-xs ${isPreflightVerified === true ? 'text-green-700' : 'text-gray-600'}`}>
                    {isPreflightVerified === true
                      ? 'Value verified. You can now create the link.'
                      : 'Verify this value before creating the link.'}
                  </span>
                )}
              </div>
              )
            })}
          </div>
        </div>
      )}

      {error !== null && <p className="text-red-600 text-sm bg-red-50 p-2 rounded">{error}</p>}
      {preflightWarning !== null && (
        <p className="text-amber-700 text-sm bg-amber-50 border border-amber-200 p-2 rounded">{preflightWarning}</p>
      )}

      <Button
        type="submit"
        disabled={isCreating || isPreflightChecking || teacherCode.trim().length < 6 || hasPersistentOptionErrors || isPreflightVerified === false}
      >
        {isCreating
          ? ((editingPersistentSession == null || createsNewLinkFromTeacherCodeChange) ? 'Creating...' : 'Saving...')
          : (createsNewLinkFromTeacherCodeChange ? 'Create New Link' : (editingPersistentSession ? 'Save Changes' : 'Generate Link'))}
      </Button>
    </form>
  )

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {sessionError && (
        <div className="mb-4 bg-red-50 border-2 border-red-200 rounded p-3">
          <p className="text-red-700 font-semibold">{sessionError}</p>
        </div>
      )}

      <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Activity Dashboard</h1>
      <p className="text-center text-gray-600 mb-8">Choose an activity to start a new session</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {activityRegistry.map((activity) => {
          const utilityLinks = (activity.utilities ?? []).filter((utility) => utility.surfaces?.includes('manage'))

          return (
            <div
              key={activity.id}
              className="rounded-lg shadow-md overflow-hidden border-2 border-gray-200 hover:border-blue-400 hover:shadow-lg transition-all flex flex-col h-full"
            >
              <div className={`${colorClasses[activity.color] || 'bg-gray-600'} text-white px-6 py-4`}>
                <h3 className="text-xl font-semibold">{activity.name}</h3>
              </div>
              <div className={`${bgColorClasses[activity.color] || 'bg-gray-50'} px-6 py-4 flex flex-col h-full`}>
                <p className="text-gray-600 mb-4">{activity.description}</p>
                <div className="flex-1" />
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => {
                      void createSession(activity.id)
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded transition-colors"
                  >
                    Start Session Now
                  </button>
                  <button
                    onClick={() => openPersistentModal(activity)}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition-colors"
                  >
                    Create Permanent Link
                  </button>
                  {utilityLinks.map((utility) => {
                    const utilityUrl = buildManageDashboardUtilityUrl(getWindowOrigin(), utility.path)
                    return (
                      <button
                        key={`${activity.id}:${utility.id}`}
                        onClick={() => {
                          if (utility.action === 'go-to-url') {
                            if (/^https?:\/\//.test(utility.path)) {
                              window.location.assign(utility.path)
                            } else {
                              void navigate(utility.path)
                            }
                            return
                          }

                          void copyToClipboard(utilityUrl)
                        }}
                        className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded transition-colors"
                        title={utility.description}
                      >
                        {utility.action === 'copy-url' && isCopied(utilityUrl) ? `✓ Copied ${utility.label}` : utility.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {persistentSessions.length > 0 && (
        <div className="mt-8 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xl font-semibold text-gray-800">Your Permanent Links</h2>
            <Button onClick={downloadPersistentLinksCSV} variant="outline" className="text-sm">
              Download CSV
            </Button>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            These links are stored in your browser cookies. If you clear cookies or use a different browser, you'll
            need to save these URLs elsewhere.
          </p>
          <div className="space-y-2">
            {persistentSessions.map((session, index) => {
              const color = getActivityColor(session.activityName)
              const bgClass = bgColorClasses[color] || 'bg-blue-50'
              const borderClass = borderColorClasses[color] || 'border-blue-200'
              const sessionKey = buildPersistentSessionKey(session.activityName, session.hash)
              const teacherCodeForSession = savedSessions[sessionKey]
              const isVisible = Boolean(visibleCodes[sessionKey])
              const fullSessionUrl = buildPersistentLinkUrl('', session.fullUrl, session.selectedOptions || {}, null)
              const optionDescriptions = describeSelectedOptions(
                getActivityById(session.activityName)?.deepLinkOptions,
                session.selectedOptions,
              )
              const entryPolicyLabel = getPersistentSessionEntryPolicyLabel(session.entryPolicy)
              const entryPolicyDescription = getPersistentSessionEntryPolicyDescription(session.entryPolicy)

              return (
                <div key={`${sessionKey}-${index}`} className={`flex items-center gap-2 ${bgClass} p-3 rounded border-2 ${borderClass}`}>
                  <button
                    type="button"
                    aria-label={`Remove permanent link for ${getActivityName(session.activityName)}`}
                    className="self-start text-gray-500 hover:text-red-600 text-xl leading-none px-2 py-1"
                    onClick={() => {
                      void removePersistentLink(session).catch((removeError) => {
                        setSessionError(removeError instanceof Error ? removeError.message : String(removeError))
                      })
                    }}
                  >
                    ×
                  </button>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-700">{getActivityName(session.activityName)}</p>
                    <p className="text-xs text-gray-500 mt-1">Entry mode: {entryPolicyLabel}. {entryPolicyDescription}</p>
                    {optionDescriptions.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">Options: {optionDescriptions.join(', ')}</p>
                    )}
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                      <span>Teacher Code:</span>
                      <code className="bg-white px-2 py-1 rounded">
                        {teacherCodeForSession ? (isVisible ? teacherCodeForSession : '•••••••') : '•••••'}
                      </code>
                      {teacherCodeForSession && (
                        <button
                          onClick={() => toggleCodeVisibility(sessionKey)}
                          className="text-blue-600 hover:text-blue-800 underline text-xs"
                          title={isVisible ? 'Hide code' : 'Show code'}
                        >
                          {isVisible ? 'hide' : 'show'}
                        </button>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      const activity = getActivityById(session.activityName)
                      if (activity) {
                        openPersistentModal(activity, session)
                      }
                    }}
                    variant="outline"
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={() => {
                      void copyToClipboard(fullSessionUrl)
                    }}
                    variant="outline"
                    className="whitespace-nowrap"
                  >
                    {isCopied(fullSessionUrl) ? '✓ Copied URL' : 'Copy URL'}
                  </Button>
                  <Button
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        window.open(fullSessionUrl, '_blank')
                      }
                    }}
                    variant="outline"
                  >
                    Open
                  </Button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <Modal
        open={showPersistentModal}
        onClose={closePersistentModal}
        title={`${editingPersistentSession ? 'Edit' : 'Create'} Permanent Link - ${selectedActivity?.name}`}
      >
        {!persistentUrl ? activePersistentModalContent : (
          <div className="flex flex-col gap-4">
            <p className="text-green-600 font-semibold">
              ✓ Permanent link {lastPersistentSubmitMode} successfully!
            </p>

            <div className="bg-gray-50 p-4 rounded border-2 border-gray-200">
              <p className="text-sm text-gray-600 mb-2 font-semibold">Your permanent URL:</p>
              <code className="text-sm break-all bg-white p-2 rounded border border-gray-300 block">{persistentUrl}</code>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  void copyToClipboard(persistentUrl)
                }}
              >
                {isCopied(persistentUrl) ? '✓ Copied!' : 'Copy to Clipboard'}
              </Button>
              <button
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    window.open(persistentUrl, '_blank')
                  }
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded transition-colors"
              >
                Open in New Tab
              </button>
            </div>

            <p className="text-sm text-gray-600">
              Save this URL! Entry mode: {getPersistentSessionEntryPolicyLabel(persistentEntryPolicy)}.
            </p>
          </div>
        )}
      </Modal>

    </div>
  )
}
